import React, { useMemo, useState, useEffect, createContext, useContext, useCallback, useRef } from 'react';
import { ConnectionProvider, WalletProvider, useWallet, useConnection } from '@solana/wallet-adapter-react';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import { 
  PhantomWalletAdapter, 
  SolflareWalletAdapter,
  CoinbaseWalletAdapter,
  TrustWalletAdapter
} from '@solana/wallet-adapter-wallets';
import { 
  clusterApiUrl, 
  LAMPORTS_PER_SOL, 
  PublicKey, 
  SystemProgram, 
  Transaction,
  TransactionInstruction
} from '@solana/web3.js';
import axios from 'axios';
import BN from 'bn.js';

// Import wallet adapter styles
import '@solana/wallet-adapter-react-ui/styles.css';

// Deposit instruction discriminator from IDL
const DEPOSIT_DISCRIMINATOR = [242, 35, 198, 137, 82, 225, 242, 182];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create context for Solana wallet features
const SolanaContext = createContext(null);

export const useSolana = () => {
  const context = useContext(SolanaContext);
  if (!context) {
    throw new Error('useSolana must be used within SolanaProvider');
  }
  return context;
};

// Inner provider that uses wallet hooks
const SolanaInnerProvider = ({ children, onWalletDisconnect }) => {
  const { publicKey, connected, signTransaction, signMessage, connect, disconnect, wallet, wallets, select, connecting } = useWallet();
  const { connection } = useConnection();
  const [balance, setBalance] = useState(null);
  const [tokenBalance, setTokenBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sessionWalletAddress, setSessionWalletAddress] = useState(null);
  const isLoggingOut = useRef(false);

  // Fetch SOL balance
  const fetchBalance = async () => {
    if (!publicKey || !connection) return null;
    try {
      const lamports = await connection.getBalance(publicKey);
      const sol = lamports / LAMPORTS_PER_SOL;
      setBalance(sol);
      return sol;
    } catch (error) {
      console.error('Error fetching balance:', error);
      return null;
    }
  };

  // Fetch SPL Token balance (supports both SPL Token and Token-2022)
  const fetchTokenBalance = async (tokenMintAddress, decimals = 6) => {
    if (!publicKey || !connection || !tokenMintAddress) {
      setTokenBalance(null);
      return null;
    }
    try {
      // Use getTokenAccountsByOwner which works for both SPL Token and Token-2022
      const tokenAccounts = await connection.getParsedTokenAccountsByOwner(
        publicKey,
        { mint: new PublicKey(tokenMintAddress) }
      );
      
      if (tokenAccounts.value.length > 0) {
        // Get balance from the first account (usually there's only one per mint)
        const accountData = tokenAccounts.value[0].account.data.parsed.info;
        const tokenAmount = accountData.tokenAmount;
        
        // Use the decimals from the response (more accurate)
        const actualBalance = tokenAmount.uiAmount || 0;
        setTokenBalance(actualBalance);
        return actualBalance;
      } else {
        // No token account means 0 balance
        setTokenBalance(0);
        return 0;
      }
    } catch (error) {
      console.error('Error fetching token balance:', error);
      setTokenBalance(0);
      return 0;
    }
  };

  // Handle logout - prevent multiple calls
  const triggerLogout = useCallback((reason) => {
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;
    
    console.log(`Wallet ${reason} - triggering logout`);
    
    if (onWalletDisconnect) {
      onWalletDisconnect();
    }
    
    setSessionWalletAddress(null);
    
    // Auto refresh page for fresh state
    setTimeout(() => {
      window.location.replace('/');
    }, 100);
  }, [onWalletDisconnect]);

  // Listen to Phantom's native accountChanged event
  useEffect(() => {
    const provider = window.phantom?.solana || window.solana;
    
    if (!provider || !sessionWalletAddress) return;

    const handleAccountChanged = (newPublicKey) => {
      if (isLoggingOut.current) return;
      
      const newAddress = newPublicKey?.toString() || null;
      console.log('Phantom accountChanged event:', { session: sessionWalletAddress, new: newAddress });
      
      // If account changed to different address or null, logout
      if (newAddress !== sessionWalletAddress) {
        triggerLogout('account changed');
      }
    };

    const handleDisconnect = () => {
      console.log('Phantom disconnect event received');
      triggerLogout('disconnected via Phantom');
    };

    // Add event listeners
    provider.on('accountChanged', handleAccountChanged);
    provider.on('disconnect', handleDisconnect);

    console.log('Phantom event listeners attached for wallet:', sessionWalletAddress);

    return () => {
      provider.removeListener('accountChanged', handleAccountChanged);
      provider.removeListener('disconnect', handleDisconnect);
    };
  }, [sessionWalletAddress, triggerLogout]);

  // Track connection state and store session wallet
  useEffect(() => {
    if (connected && publicKey) {
      const currentKey = publicKey.toString();
      
      // Only set session wallet if not already set (first connection after login)
      if (!sessionWalletAddress) {
        setSessionWalletAddress(currentKey);
        console.log('Session wallet set:', currentKey);
      }
      
      fetchBalance();
    } else {
      setBalance(null);
      
      // If we had a session and now disconnected, trigger logout
      if (sessionWalletAddress && !connected) {
        triggerLogout('disconnected');
      }
    }
  }, [connected, publicKey, sessionWalletAddress, triggerLogout]);

  // Custom disconnect that also clears session
  const handleDisconnect = useCallback(async () => {
    try {
      await disconnect();
      // Clear session on disconnect
      if (onWalletDisconnect) {
        onWalletDisconnect();
      }
    } catch (error) {
      console.error('Error disconnecting wallet:', error);
    }
  }, [disconnect, onWalletDisconnect]);

  // Send SOL transaction
  const sendSOL = async (amountSOL, token) => {
    if (!publicKey || !signTransaction) {
      throw new Error('Wallet not connected');
    }

    setLoading(true);
    try {
      // Build transaction on backend
      const buildRes = await axios.post(`${API}/solana/transaction/build`, {
        sender_pubkey: publicKey.toString(),
        amount_sol: amountSOL
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { transaction: transactionBase64 } = buildRes.data;

      // Decode and sign transaction
      const { Transaction } = await import('@solana/web3.js');
      const transactionBuffer = Buffer.from(transactionBase64, 'base64');
      const transaction = Transaction.from(transactionBuffer);

      // Sign with wallet
      const signedTx = await signTransaction(transaction);

      // Send signed transaction
      const sendRes = await axios.post(`${API}/solana/transaction/send`, {
        transaction: Buffer.from(signedTx.serialize()).toString('base64'),
        amount_sol: amountSOL
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh balance
      await fetchBalance();

      return sendRes.data;
    } finally {
      setLoading(false);
    }
  };

  // Send SOL via Smart Contract deposit instruction
  const sendSOLViaContract = async (amountSOL, token, programId, fallbackTreasury) => {
    if (!publicKey || !signTransaction) {
      throw new Error('Wallet not connected');
    }
    if (!programId) {
      throw new Error('Smart contract Program ID not configured');
    }

    setLoading(true);
    try {
      const programPubkey = new PublicKey(programId);
      
      // Derive config PDA
      const [configPda] = PublicKey.findProgramAddressSync(
        [Buffer.from('config')],
        programPubkey
      );

      // ============================================================
      // STEP 1: Read config account from on-chain to get real treasury
      // ============================================================
      console.log('Fetching config from on-chain...', configPda.toString());
      
      const configAccountInfo = await connection.getAccountInfo(configPda);
      if (!configAccountInfo) {
        throw new Error('Config account not found on-chain. Smart contract may not be initialized.');
      }

      // Parse config data
      // Layout (from IDL):
      // - 8 bytes: discriminator
      // - 32 bytes: authority (pubkey)
      // - 1 byte + optional 32 bytes: pending_authority (Option<Pubkey>)
      // - 32 bytes: treasury (pubkey)
      // - 2 bytes: cashback_bps (u16)
      // - 32 bytes: pumpswap_program (pubkey)
      // - 1 byte: paused (bool)
      // - 1 byte: bump (u8)
      
      const configData = configAccountInfo.data;
      let offset = 8; // Skip discriminator
      
      // Skip authority (32 bytes)
      offset += 32;
      
      // Skip pending_authority (Option<Pubkey>)
      const hasPendingAuthority = configData[offset] === 1;
      offset += 1;
      if (hasPendingAuthority) {
        offset += 32;
      }
      
      // Read treasury (32 bytes) - use PublicKey directly from bytes
      const treasuryBytes = configData.slice(offset, offset + 32);
      const treasuryPubkey = new PublicKey(treasuryBytes);
      offset += 32;
      
      // Read cashback bps (2 bytes)
      // Skip for now
      offset += 2;
      
      // Skip pumpswap_program (32 bytes)
      offset += 32;
      
      // Read paused status (1 byte)
      const isPaused = configData[offset] === 1;
      
      if (isPaused) {
        throw new Error('Deposits are currently paused by the contract admin.');
      }

      console.log('On-chain config:', {
        configPda: configPda.toString(),
        treasury: treasuryPubkey.toString(),
        isPaused
      });
      
      // Calculate amount in lamports (u64)
      const amountLamports = new BN(Math.floor(amountSOL * LAMPORTS_PER_SOL));

      console.log('Smart Contract Deposit:', {
        programId: programPubkey.toString(),
        configPda: configPda.toString(),
        treasury: treasuryPubkey.toString(),
        depositor: publicKey.toString(),
        amountSOL,
        amountLamports: amountLamports.toString()
      });

      // ============================================================
      // STEP 2: Build the deposit instruction
      // ============================================================
      // Format: [8 bytes discriminator] + [8 bytes amount as little-endian u64]
      const instructionData = Buffer.alloc(16);
      
      // Write discriminator (first 8 bytes)
      Buffer.from(DEPOSIT_DISCRIMINATOR).copy(instructionData, 0);
      
      // Write amount as little-endian u64 (next 8 bytes)
      const amountBuffer = amountLamports.toArrayLike(Buffer, 'le', 8);
      amountBuffer.copy(instructionData, 8);

      // Create the instruction with accounts in order from IDL:
      // 1. depositor (writable, signer)
      // 2. config (PDA)
      // 3. treasury (writable)
      // 4. system_program
      const depositInstruction = new TransactionInstruction({
        keys: [
          { pubkey: publicKey, isSigner: true, isWritable: true },      // depositor
          { pubkey: configPda, isSigner: false, isWritable: false },    // config
          { pubkey: treasuryPubkey, isSigner: false, isWritable: true }, // treasury
          { pubkey: SystemProgram.programId, isSigner: false, isWritable: false }, // system_program
        ],
        programId: programPubkey,
        data: instructionData,
      });

      // ============================================================
      // STEP 3: Create and send transaction
      // ============================================================
      const tx = new Transaction().add(depositInstruction);

      // Get latest blockhash
      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
      tx.recentBlockhash = blockhash;
      tx.feePayer = publicKey;

      // Sign the transaction
      const signedTx = await signTransaction(tx);

      // Send and confirm transaction
      // Use skipPreflight: true to avoid wallet adding extra instructions
      const signature = await connection.sendRawTransaction(signedTx.serialize(), {
        skipPreflight: true,
        preflightCommitment: 'confirmed',
        maxRetries: 3
      });

      console.log('Transaction sent:', signature);

      // Wait for confirmation
      console.log('Waiting for confirmation...');
      const confirmation = await connection.confirmTransaction({
        signature,
        blockhash,
        lastValidBlockHeight
      }, 'confirmed');
      
      console.log('Transaction confirmed:', confirmation);

      // ============================================================
      // STEP 4: Notify backend about the deposit
      // ============================================================
      console.log('Notifying backend about deposit...');
      let sendRes;
      try {
        sendRes = await axios.post(`${API}/solana/contract-deposit/confirm`, {
          signature,
          amount_sol: amountSOL,
          program_id: programId
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Backend response:', sendRes.data);
      } catch (backendError) {
        console.error('Backend confirmation failed:', backendError);
        // Transaction was successful on-chain, but backend failed
        // Return partial success with signature so user can verify on explorer
        return {
          signature,
          coins_credited: 0,
          new_balance: 0,
          explorer_url: `https://solscan.io/tx/${signature}?cluster=devnet`,
          warning: 'Transaction successful on-chain but backend confirmation failed. Please contact support with this signature.'
        };
      }

      // Refresh balance
      await fetchBalance();

      return {
        ...sendRes.data,
        signature
      };
    } finally {
      setLoading(false);
    }
  };

  // Discriminator for deposit_with_cashback instruction from IDL
  const DEPOSIT_WITH_CASHBACK_DISCRIMINATOR = [237, 59, 146, 236, 172, 204, 24, 154];

  // Send SOL with Cashback via Smart Contract (deposit_with_cashback instruction)
  // Uses @pump-fun/pump-swap-sdk to derive correct PumpSwap accounts
  const sendSOLWithCashback = async (
    amountSOL, 
    token, 
    programId, 
    cashbackSettings // { pumpswapProgram, cashbackTokenMint, treasuryAddress, isDevnet }
  ) => {
    if (!publicKey || !signTransaction) {
      throw new Error('Wallet not connected');
    }
    if (!programId) {
      throw new Error('Smart contract Program ID not configured');
    }
    if (!cashbackSettings?.pumpswapProgram || !cashbackSettings?.cashbackTokenMint) {
      throw new Error('PumpSwap cashback settings not configured (need pumpswapProgram and cashbackTokenMint)');
    }

    const isDevnet = cashbackSettings.isDevnet || false;
    
    setLoading(true);
    try {
      // Import SDK with PDA helpers
      const { 
        OnlinePumpAmmSdk, 
        GLOBAL_CONFIG_PDA,
        PUMP_AMM_EVENT_AUTHORITY_PDA,
        GLOBAL_VOLUME_ACCUMULATOR_PDA,
        PUMP_AMM_FEE_CONFIG_PDA,
        PUMP_FEE_PROGRAM_ID,
        userVolumeAccumulatorPda,
        coinCreatorVaultAuthorityPda,
        coinCreatorVaultAtaPda,
        poolV2Pda,
        canonicalPumpPoolPda
      } = await import('@pump-fun/pump-swap-sdk');
      
      const { 
        getAssociatedTokenAddress,
        createAssociatedTokenAccountInstruction,
        TOKEN_2022_PROGRAM_ID,
        TOKEN_PROGRAM_ID,
        ASSOCIATED_TOKEN_PROGRAM_ID,
        getAccount
      } = await import('@solana/spl-token');

      const programPubkey = new PublicKey(programId);
      const pumpswapProgramPubkey = new PublicKey(cashbackSettings.pumpswapProgram);
      const cashbackTokenMintPubkey = new PublicKey(cashbackSettings.cashbackTokenMint);
      
      // Derive pool address from token mint using SDK (canonical first)
      let poolPubkey = canonicalPumpPoolPda(cashbackTokenMintPubkey);
      if (!poolPubkey) {
        throw new Error('Failed to derive pool address from token mint');
      }
      console.log('Pool derived from token mint (canonical):', poolPubkey.toString());

      // Fallback: if canonical pool not found on-chain, search by base_mint.
      // pump.fun tokens migrated outside the canonical flow use a custom creator,
      // so the canonical PDA misses them.
      const canonicalPoolInfo = await connection.getAccountInfo(poolPubkey);
      if (!canonicalPoolInfo) {
        console.log('Canonical pool not found, searching PumpSwap pools by base_mint…');
        const found = await connection.getProgramAccounts(pumpswapProgramPubkey, {
          encoding: 'base64',
          filters: [
            { memcmp: { offset: 43, bytes: cashbackTokenMintPubkey.toString() } },
          ],
        });
        if (!found || found.length === 0) {
          throw new Error('No PumpSwap pool found for the configured cashback token.');
        }
        const WSOL_STR = 'So11111111111111111111111111111111111111112';
        const wsolPool = found.find((p) => {
          try {
            const qm = new PublicKey(p.account.data.slice(75, 107)).toString();
            return qm === WSOL_STR;
          } catch { return false; }
        }) || found[0];
        poolPubkey = wsolPool.pubkey;
        console.log(`Found non-canonical pool (${found.length} total):`, poolPubkey.toString());
      }

      // Fixed addresses
      const WSOL_MINT = new PublicKey('So11111111111111111111111111111111111111112');
      // Use TOKEN_PROGRAM_ID from spl-token for WSOL (not Token-2022)

      console.log(`=== Deposit with Cashback via Smart Contract (${isDevnet ? 'Devnet' : 'Mainnet'}) ===`);
      console.log('Program ID:', programPubkey.toString());
      console.log('PumpSwap Program:', pumpswapProgramPubkey.toString());
      console.log('Pool:', poolPubkey.toString());

      // ============================================================
      // STEP 1: Use SDK to get pool state
      // ============================================================
      console.log('Using PumpSwap SDK to fetch pool state...');
      const onlineSdk = new OnlinePumpAmmSdk(connection);
      
      const swapState = await onlineSdk.swapSolanaState(poolPubkey, publicKey);
      if (!swapState || !swapState.pool) {
        throw new Error('Pool not found via SDK');
      }
      
      // Extract pool data from SDK
      const poolBaseVault = swapState.pool.poolBaseTokenAccount;
      const poolQuoteVault = swapState.pool.poolQuoteTokenAccount;
      const coinCreator = swapState.pool.coinCreator;

      console.log('SDK Pool State:', {
        poolBaseVault: poolBaseVault.toString(),
        poolQuoteVault: poolQuoteVault.toString(),
        coinCreator: coinCreator.toString()
      });

      // ============================================================
      // STEP 2: Use SDK PDAs for PumpSwap accounts
      // ============================================================
      console.log('Using SDK PDAs for PumpSwap accounts...');
      
      // Debug: Log all imported SDK values
      console.log('SDK Imports Debug:', {
        GLOBAL_CONFIG_PDA: GLOBAL_CONFIG_PDA?.toString() || 'UNDEFINED',
        PUMP_AMM_EVENT_AUTHORITY_PDA: PUMP_AMM_EVENT_AUTHORITY_PDA?.toString() || 'UNDEFINED',
        GLOBAL_VOLUME_ACCUMULATOR_PDA: GLOBAL_VOLUME_ACCUMULATOR_PDA?.toString() || 'UNDEFINED',
        PUMP_AMM_FEE_CONFIG_PDA: PUMP_AMM_FEE_CONFIG_PDA?.toString() || 'UNDEFINED',
        PUMP_FEE_PROGRAM_ID: PUMP_FEE_PROGRAM_ID?.toString() || 'UNDEFINED',
        userVolumeAccumulatorPda: typeof userVolumeAccumulatorPda,
        coinCreatorVaultAuthorityPda: typeof coinCreatorVaultAuthorityPda,
        coinCreatorVaultAtaPda: typeof coinCreatorVaultAtaPda
      });
      
      // SDK provides these PDAs directly - validate they exist
      if (!GLOBAL_CONFIG_PDA) throw new Error('SDK GLOBAL_CONFIG_PDA is undefined');
      if (!PUMP_AMM_EVENT_AUTHORITY_PDA) throw new Error('SDK PUMP_AMM_EVENT_AUTHORITY_PDA is undefined');
      if (!GLOBAL_VOLUME_ACCUMULATOR_PDA) throw new Error('SDK GLOBAL_VOLUME_ACCUMULATOR_PDA is undefined');
      if (!PUMP_AMM_FEE_CONFIG_PDA) throw new Error('SDK PUMP_AMM_FEE_CONFIG_PDA is undefined');
      if (!PUMP_FEE_PROGRAM_ID) throw new Error('SDK PUMP_FEE_PROGRAM_ID is undefined');
      if (typeof userVolumeAccumulatorPda !== 'function') throw new Error('SDK userVolumeAccumulatorPda is not a function');
      if (typeof coinCreatorVaultAuthorityPda !== 'function') throw new Error('SDK coinCreatorVaultAuthorityPda is not a function');
      if (typeof coinCreatorVaultAtaPda !== 'function') throw new Error('SDK coinCreatorVaultAtaPda is not a function');
      
      const globalConfigPda = GLOBAL_CONFIG_PDA;
      const eventAuthority = PUMP_AMM_EVENT_AUTHORITY_PDA;
      const globalVolumeAccumulator = GLOBAL_VOLUME_ACCUMULATOR_PDA;
      const feeConfig = PUMP_AMM_FEE_CONFIG_PDA;
      const feeProgram = PUMP_FEE_PROGRAM_ID;
      
      // SDK helper for user volume accumulator
      const userVolumeAccumulator = userVolumeAccumulatorPda(publicKey);
      if (!userVolumeAccumulator) throw new Error('userVolumeAccumulatorPda returned undefined');
      
      // SDK helpers for coin creator vault (use TOKEN_PROGRAM_ID for WSOL)
      const coinCreatorVaultAuthority = coinCreatorVaultAuthorityPda(coinCreator);
      if (!coinCreatorVaultAuthority) throw new Error('coinCreatorVaultAuthorityPda returned undefined');
      
      const coinCreatorVaultAta = coinCreatorVaultAtaPda(coinCreatorVaultAuthority, WSOL_MINT, TOKEN_PROGRAM_ID);
      if (!coinCreatorVaultAta) throw new Error('coinCreatorVaultAtaPda returned undefined');

      console.log('SDK PDAs:', {
        globalConfigPda: globalConfigPda.toString(),
        eventAuthority: eventAuthority.toString(),
        globalVolumeAccumulator: globalVolumeAccumulator.toString(),
        feeConfig: feeConfig.toString(),
        feeProgram: feeProgram.toString(),
        userVolumeAccumulator: userVolumeAccumulator.toString(),
        coinCreatorVaultAuthority: coinCreatorVaultAuthority.toString(),
        coinCreatorVaultAta: coinCreatorVaultAta.toString()
      });

      // ============================================================
      // STEP 3: Read smart contract config
      // ============================================================
      const [configPda] = PublicKey.findProgramAddressSync(
        [Buffer.from('config')],
        programPubkey
      );
      
      const configAccountInfo = await connection.getAccountInfo(configPda);
      if (!configAccountInfo) {
        throw new Error('Config account not found. Smart contract may not be initialized.');
      }

      // Config layout (from IDL):
      //   discriminator(8) | authority(32) | pending_authority(Option<Pubkey>: 1+32?) |
      //   treasury(32) | cashback_bps(u16:2) | cashback_token_mint(Option<Pubkey>: 1+32?) |
      //   pumpswap_program(32) | paused(bool:1) | bump(u8:1)
      const configData = configAccountInfo.data;
      let offset = 8;          // skip discriminator
      offset += 32;            // skip authority
      
      // pending_authority: Option<Pubkey>
      const hasPendingAuthority = configData[offset] === 1;
      offset += 1;
      if (hasPendingAuthority) offset += 32;
      
      // treasury
      const treasuryPubkey = new PublicKey(configData.slice(offset, offset + 32));
      offset += 32;
      
      // cashback_bps
      const cashbackBps = configData.readUInt16LE(offset);
      offset += 2;
      
      // cashback_token_mint: Option<Pubkey>
      const hasCashbackTokenMint = configData[offset] === 1;
      offset += 1;
      let allowedCashbackMint = null;
      if (hasCashbackTokenMint) {
        allowedCashbackMint = new PublicKey(configData.slice(offset, offset + 32));
        offset += 32;
      }
      
      // pumpswap_program (skip)
      offset += 32;
      
      // paused
      const isPaused = configData[offset] === 1;
      
      if (isPaused) {
        throw new Error('Deposits are currently paused.');
      }

      // Validate cashback token mint if restriction set on-chain
      if (allowedCashbackMint && !allowedCashbackMint.equals(cashbackTokenMintPubkey)) {
        throw new Error(
          `Cashback token mint mismatch. Smart contract allows only ${allowedCashbackMint.toString()}, ` +
          `but settings configured ${cashbackTokenMintPubkey.toString()}.`
        );
      }

      console.log('Contract Config:', { 
        treasury: treasuryPubkey.toString(), 
        cashbackBps,
        allowedCashbackMint: allowedCashbackMint?.toString() || 'any'
      });

      // ============================================================
      // STEP 4: Pick fee recipients exactly as the working TS script does
      //   - protocolFeeRecipient: from protocolFeeRecipients (or reserved if mayhem)
      //   - feeRecipient (NEW): from buybackFeeRecipients (used in remaining accounts)
      // Both filtered to non-default, random pick.
      // ============================================================
      const PUBKEY_DEFAULT = PublicKey.default;
      const pickRandom = (arr, label) => {
        if (!arr || !Array.isArray(arr)) {
          throw new Error(`${label}: array missing on globalConfig`);
        }
        const valid = arr.filter((k) => k && !k.equals(PUBKEY_DEFAULT));
        if (valid.length === 0) {
          throw new Error(`${label}: no non-default entry in array of ${arr.length}`);
        }
        return valid[Math.floor(Math.random() * valid.length)];
      };

      const gc = swapState.globalConfig;
      const isMayhemMode = !!swapState.pool.isMayhemMode;
      const isCashbackCoin = !!swapState.pool.isCashbackCoin;
      const hasCoinCreator = coinCreator && !coinCreator.equals(PUBKEY_DEFAULT);

      const protocolFeeRecipient = pickRandom(
        isMayhemMode
          ? [gc.reservedFeeRecipient, ...(gc.reservedFeeRecipients || [])]
          : gc.protocolFeeRecipients,
        'protocolFeeRecipient'
      );

      // NEW (BREAKING_FEE_RECIPIENT.md): buyback fee recipient (used in remaining accounts)
      const feeRecipient = pickRandom(
        gc.buybackFeeRecipients,
        'feeRecipient (buybackFeeRecipients)'
      );

      console.log('Pool flags:', { isMayhemMode, isCashbackCoin, hasCoinCreator });
      console.log('Protocol Fee Recipient:', protocolFeeRecipient.toString());
      console.log('Buyback Fee Recipient:', feeRecipient.toString());

      // ============================================================
      // STEP 5: Detect token program and derive ATAs
      // ============================================================
      const mintAccountInfo = await connection.getAccountInfo(cashbackTokenMintPubkey);
      if (!mintAccountInfo) {
        throw new Error('Cashback token mint not found.');
      }
      
      const baseTokenProgram = mintAccountInfo.owner;
      const isToken2022 = baseTokenProgram.equals(TOKEN_2022_PROGRAM_ID);
      console.log('Token Program:', isToken2022 ? 'Token-2022' : 'SPL Token');

      // User's cashback token ATA
      const userBaseTokenAccount = await getAssociatedTokenAddress(
        cashbackTokenMintPubkey,
        publicKey,
        false,
        baseTokenProgram,
        ASSOCIATED_TOKEN_PROGRAM_ID
      );

      // User's WSOL ATA (use TOKEN_PROGRAM_ID, not Token-2022)
      const userQuoteTokenAccount = await getAssociatedTokenAddress(
        WSOL_MINT,
        publicKey,
        false,
        TOKEN_PROGRAM_ID,
        ASSOCIATED_TOKEN_PROGRAM_ID
      );

      // Protocol fee recipient's WSOL ATA (fee paid in quote token = WSOL)
      const protocolFeeRecipientTokenAccount = await getAssociatedTokenAddress(
        WSOL_MINT,              // quoteMint = WSOL
        protocolFeeRecipient,   // dari swapState
        true,                   // allowOwnerOffCurve
        TOKEN_PROGRAM_ID,       // WSOL uses standard Token Program
        ASSOCIATED_TOKEN_PROGRAM_ID
      );

      console.log('Derived ATAs:', {
        userBaseTokenAccount: userBaseTokenAccount.toString(),
        userQuoteTokenAccount: userQuoteTokenAccount.toString(),
        protocolFeeRecipientTokenAccount: protocolFeeRecipientTokenAccount.toString()
      });

      // ============================================================
      // STEP 6: Build transaction
      // ============================================================
      const tx = new Transaction();
      
      // Create user's token ATA if needed
      try {
        await getAccount(connection, userBaseTokenAccount, 'confirmed', baseTokenProgram);
        console.log('User token ATA exists');
      } catch {
        console.log('Creating user token ATA...');
        tx.add(
          createAssociatedTokenAccountInstruction(
            publicKey,
            userBaseTokenAccount,
            publicKey,
            cashbackTokenMintPubkey,
            baseTokenProgram,
            ASSOCIATED_TOKEN_PROGRAM_ID
          )
        );
      }

      // Build deposit_with_cashback instruction
      // IDL: args = [{ name: 'amount', type: 'u64' }] - only 1 argument (total deposit amount)
      const amountLamports = new BN(Math.floor(amountSOL * LAMPORTS_PER_SOL));
      
      // Instruction data: 8 bytes discriminator + 8 bytes amount = 16 bytes total
      const instructionData = Buffer.alloc(16);
      Buffer.from(DEPOSIT_WITH_CASHBACK_DISCRIMINATOR).copy(instructionData, 0);
      amountLamports.toArrayLike(Buffer, 'le', 8).copy(instructionData, 8);

      console.log('Instruction:', { amount: amountSOL, lamports: amountLamports.toString() });

      // 25 named accounts for deposit_with_cashback (using SDK PDAs)
      const accounts = [
        { pubkey: publicKey, isSigner: true, isWritable: true },                          // 1. depositor
        { pubkey: configPda, isSigner: false, isWritable: false },                        // 2. config
        { pubkey: treasuryPubkey, isSigner: false, isWritable: true },                    // 3. treasury
        { pubkey: poolPubkey, isSigner: false, isWritable: true },                        // 4. pool
        { pubkey: globalConfigPda, isSigner: false, isWritable: false },                  // 5. global_config (SDK PDA)
        { pubkey: cashbackTokenMintPubkey, isSigner: false, isWritable: false },          // 6. base_mint
        { pubkey: WSOL_MINT, isSigner: false, isWritable: false },                        // 7. quote_mint
        { pubkey: userBaseTokenAccount, isSigner: false, isWritable: true },              // 8. user_base_token_account
        { pubkey: userQuoteTokenAccount, isSigner: false, isWritable: true },             // 9. user_quote_token_account
        { pubkey: poolBaseVault, isSigner: false, isWritable: true },                     // 10. pool_base_token_account (SDK)
        { pubkey: poolQuoteVault, isSigner: false, isWritable: true },                    // 11. pool_quote_token_account (SDK)
        { pubkey: protocolFeeRecipient, isSigner: false, isWritable: false },             // 12. protocol_fee_recipient
        { pubkey: protocolFeeRecipientTokenAccount, isSigner: false, isWritable: true },  // 13. protocol_fee_recipient_token_account
        { pubkey: baseTokenProgram, isSigner: false, isWritable: false },                 // 14. base_token_program
        { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },                 // 15. quote_token_program (WSOL uses standard)
        { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },          // 16. system_program
        { pubkey: ASSOCIATED_TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },      // 17. associated_token_program
        { pubkey: eventAuthority, isSigner: false, isWritable: false },                   // 18. event_authority (SDK PDA)
        { pubkey: pumpswapProgramPubkey, isSigner: false, isWritable: false },            // 19. pumpswap_program
        { pubkey: coinCreatorVaultAta, isSigner: false, isWritable: true },               // 20. coin_creator_vault_ata (SDK)
        { pubkey: coinCreatorVaultAuthority, isSigner: false, isWritable: false },        // 21. coin_creator_vault_authority (SDK)
        { pubkey: globalVolumeAccumulator, isSigner: false, isWritable: false },          // 22. global_volume_accumulator (SDK PDA)
        { pubkey: userVolumeAccumulator, isSigner: false, isWritable: true },             // 23. user_volume_accumulator (SDK)
        { pubkey: feeConfig, isSigner: false, isWritable: false },                        // 24. fee_config (SDK PDA)
        { pubkey: feeProgram, isSigner: false, isWritable: false },                       // 25. fee_program (SDK)
      ];

      // Remaining accounts (mirrors @pump-fun/pump-swap-sdk buyInstructionsNoPool + 
      // BREAKING_FEE_RECIPIENT.md). Order matters:
      //   1. userVolumeAccumulator WSOL ATA  — only if pool.isCashbackCoin
      //   2. poolV2(baseMint)                — only if pool.coinCreator != default
      //   3. feeRecipient (buyback)          — always
      //   4. feeRecipient WSOL ATA           — always
      const userVolumeAccumulatorWsolAta = await getAssociatedTokenAddress(
        WSOL_MINT,
        userVolumeAccumulator,
        true,
        TOKEN_PROGRAM_ID,
        ASSOCIATED_TOKEN_PROGRAM_ID
      );
      const feeRecipientWsolAta = await getAssociatedTokenAddress(
        WSOL_MINT,
        feeRecipient,
        true,
        TOKEN_PROGRAM_ID,
        ASSOCIATED_TOKEN_PROGRAM_ID
      );
      const poolV2 = poolV2Pda(cashbackTokenMintPubkey);

      const remainingAccounts = [];
      if (isCashbackCoin) {
        remainingAccounts.push({ pubkey: userVolumeAccumulatorWsolAta, isSigner: false, isWritable: true });
      }
      if (hasCoinCreator) {
        remainingAccounts.push({ pubkey: poolV2, isSigner: false, isWritable: false });
      }
      remainingAccounts.push({ pubkey: feeRecipient, isSigner: false, isWritable: false });
      remainingAccounts.push({ pubkey: feeRecipientWsolAta, isSigner: false, isWritable: true });

      console.log(`Remaining accounts (${remainingAccounts.length}):`);
      remainingAccounts.forEach((a, i) => {
        console.log(`  [${i}] ${a.pubkey.toString()}${a.isWritable ? ' (mut)' : ''}`);
      });
      accounts.push(...remainingAccounts);

      console.log(`=== ${accounts.length} Accounts (25 named + ${remainingAccounts.length} remaining) ===`);
      accounts.forEach((acc, i) => {
        console.log(`${i + 1}. ${acc.pubkey.toString()}${acc.isWritable ? ' [mut]' : ''}${acc.isSigner ? ' [signer]' : ''}`);
      });

      tx.add(new TransactionInstruction({
        keys: accounts,
        programId: programPubkey,
        data: instructionData,
      }));

      // ============================================================
      // STEP 7: Sign and send transaction
      // ============================================================
      console.log('Getting blockhash...');
      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('finalized');
      tx.recentBlockhash = blockhash;
      tx.feePayer = publicKey;

      console.log('Signing transaction...');
      const signedTx = await signTransaction(tx);

      console.log('Sending transaction...');
      const signature = await connection.sendRawTransaction(signedTx.serialize(), {
        skipPreflight: true,
        preflightCommitment: 'confirmed',
        maxRetries: 3
      });

      console.log('Transaction sent:', signature);
      
      const confirmation = await connection.confirmTransaction({
        signature,
        blockhash,
        lastValidBlockHeight
      }, 'confirmed');

      if (confirmation.value.err) {
        // Fetch transaction logs untuk debug
        console.log('Transaction failed, fetching logs...');
        let logs = [];
        let errorDetails = null;
        
        try {
          const txDetails = await connection.getTransaction(signature, {
            commitment: 'confirmed',
            maxSupportedTransactionVersion: 0
          });
          
          if (txDetails?.meta?.logMessages) {
            logs = txDetails.meta.logMessages;
            console.log('=== Transaction Logs ===');
            logs.forEach((log, i) => console.log(`${i}: ${log}`));
          }
          
          // Parse error dari logs
          const errorLog = logs.find(log => 
            log.includes('Error') || 
            log.includes('failed') || 
            log.includes('panicked') ||
            log.includes('Program log:')
          );
          
          if (errorLog) {
            errorDetails = errorLog;
          }
        } catch (logErr) {
          console.warn('Failed to fetch transaction logs:', logErr);
        }

        // Return detailed error info
        const errorInfo = {
          success: false,
          error: true,
          signature,
          explorer_url: `https://solscan.io/tx/${signature}${isDevnet ? '?cluster=devnet' : ''}`,
          error_code: confirmation.value.err,
          error_message: JSON.stringify(confirmation.value.err),
          logs: logs,
          error_details: errorDetails,
          accounts_used: accounts.map((acc, i) => ({
            index: i + 1,
            pubkey: acc.pubkey.toString(),
            writable: acc.isWritable,
            signer: acc.isSigner
          }))
        };

        console.log('Error Info:', errorInfo);
        
        // Throw error with detailed info
        const detailedError = new Error(`Transaction failed: ${JSON.stringify(confirmation.value.err)}`);
        detailedError.txInfo = errorInfo;
        throw detailedError;
      }

      console.log('✅ Transaction confirmed!');

      // Get the cashback token amount received by checking user's token balance change
      let cashbackTokenAmount = null;
      try {
        // Fetch user's token balance after the transaction
        const tokenAccounts = await connection.getParsedTokenAccountsByOwner(
          publicKey,
          { mint: cashbackTokenMintPubkey }
        );
        
        if (tokenAccounts.value.length > 0) {
          const tokenBalance = tokenAccounts.value[0].account.data.parsed.info.tokenAmount.uiAmount || 0;
          console.log('User token balance after tx:', tokenBalance);
          
          // Try to get the token amount from transaction logs (parse inner instructions)
          try {
            const txDetails = await connection.getTransaction(signature, {
              commitment: 'confirmed',
              maxSupportedTransactionVersion: 0
            });
            
            if (txDetails?.meta?.postTokenBalances && txDetails?.meta?.preTokenBalances) {
              // Find the user's token account balance change
              const userTokenAccount = userBaseTokenAccount.toString();
              
              const preBalance = txDetails.meta.preTokenBalances.find(
                b => b.owner === publicKey.toString() && b.mint === cashbackTokenMintPubkey.toString()
              );
              const postBalance = txDetails.meta.postTokenBalances.find(
                b => b.owner === publicKey.toString() && b.mint === cashbackTokenMintPubkey.toString()
              );
              
              if (postBalance) {
                const preBal = preBalance?.uiTokenAmount?.uiAmount || 0;
                const postBal = postBalance?.uiTokenAmount?.uiAmount || 0;
                cashbackTokenAmount = postBal - preBal;
                console.log(`Cashback tokens received: ${cashbackTokenAmount} (${preBal} -> ${postBal})`);
              }
            }
          } catch (txErr) {
            console.warn('Could not parse token balance from tx:', txErr);
          }
        }
      } catch (balErr) {
        console.warn('Could not fetch token balance:', balErr);
      }

      // Notify backend with cashback info
      let backendResult = null;
      try {
        const depositRes = await axios.post(`${API}/solana/contract-deposit/confirm`, {
          signature,
          amount_sol: amountSOL,
          program_id: programId,
          deposit_type: 'cashback',
          cashback_bps: cashbackBps,
          cashback_token_amount: cashbackTokenAmount,
          cashback_token_mint: cashbackTokenMintPubkey.toString()
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        backendResult = depositRes.data;
        console.log('Backend response:', backendResult);
      } catch (err) {
        console.warn('Backend notification failed:', err);
      }

      await fetchBalance();

      const explorerCluster = isDevnet ? '?cluster=devnet' : '';
      return { 
        success: true, 
        signature,
        cashback_bps: cashbackBps,
        cashback_token_amount: cashbackTokenAmount,
        explorer_url: `https://solscan.io/tx/${signature}${explorerCluster}`,
        ...(backendResult || {})
      };
      
    } catch (error) {
      console.error('Deposit with cashback error:', error);
      
      // Jika error punya txInfo, propagate ke caller
      if (error.txInfo) {
        const enrichedError = new Error(error.message);
        enrichedError.txInfo = error.txInfo;
        throw enrichedError;
      }
      
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    publicKey,
    connected,
    balance,
    tokenBalance,
    loading,
    fetchBalance,
    fetchTokenBalance,
    sendSOL,
    sendSOLViaContract,
    sendSOLWithCashback,
    signMessage,
    signTransaction,
    connect,
    disconnect: handleDisconnect,
    wallet,
    wallets,
    select,
    connecting
  };

  return (
    <SolanaContext.Provider value={value}>
      {children}
    </SolanaContext.Provider>
  );
};

// Main Solana Wallet Provider
export const SolanaWalletProvider = ({ children, rpcUrl, network = 'mainnet-beta', onWalletDisconnect }) => {
  // Check if user has logged out - don't auto connect if so
  const [shouldAutoConnect, setShouldAutoConnect] = useState(() => {
    return localStorage.getItem('wallet_auto_connect') !== 'false';
  });

  // Use custom RPC or default
  const endpoint = useMemo(() => {
    if (rpcUrl) return rpcUrl;
    return clusterApiUrl(network);
  }, [rpcUrl, network]);

  // Supported wallets (browser extension & mobile only, no USB/hardware)
  const wallets = useMemo(() => [
    new PhantomWalletAdapter(),
    new SolflareWalletAdapter(),
    new CoinbaseWalletAdapter(),
    new TrustWalletAdapter(),
  ], []);

  return (
    <ConnectionProvider endpoint={endpoint}>
      <WalletProvider wallets={wallets} autoConnect={shouldAutoConnect}>
        <WalletModalProvider>
          <SolanaInnerProvider onWalletDisconnect={onWalletDisconnect}>
            {children}
          </SolanaInnerProvider>
        </WalletModalProvider>
      </WalletProvider>
    </ConnectionProvider>
  );
};

export default SolanaWalletProvider;
